import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import ItemGrid from './components/ItemGrid'
import {
  Font,
  FontSize,
  ICategoryResponse,
  SubCategoryFormat,
} from '../types/organisation'
import {
  IAddToBasket,
  IColorTheme,
  IGiftItemResponse,
} from '../types/itemTypes'
import { CurrencyCode } from '../types/API'
import CategoryGrid from './components/CategoryGrid'
import { filterGiftItems } from './components/itemGridHelper'
import CategorySelect from './components/select/CategorySelect'
import OrderSelect, { ItemOrder } from './components/select/OrderSelect'
import { getFontSize, getItemColorTheme, isTicket } from '../helpers/itemHelper'
import { getFont } from '../helpers/dataTransformer'
import { scrollToTop } from '../helpers/scrollHelper'
import { useNotFirstRender } from '../helpers/firstRender'
import SubCategories from './components/select/SubCategories'
import SubCategorySelect from './components/select/SubCategorySelect'
import BackButton from './components/BackButton'

const Styles = styled.div`
  background-color: ${(props): string => props.theme.itemColors.colourPageBackground};
  font-family: ${(props): string => props.theme.font};
`
const Categories = styled.div`
  position: relative;
  max-width: 1068px;
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 0 auto;
  padding: 20px 25px 0;
  > div {
    @media (min-width: 551px) {
      max-width: ${(props): string => (props.theme.shrink ? '300px' : '380px')};
    }
    flex: 1;
  }
  @media (max-width: 320px) {
    padding: 0 5px;
  }
  @media (max-width: 550px) {
    flex-direction: column;
  }
`
const EmbeddedSubcategorySelect = styled.div`
  max-width: 380px;
  margin: 0 auto;
  padding: 20px 25px 0;
  @media (max-width: 320px) {
    padding: 0 5px;
  }
`
const IntroText = styled.div`
  text-align: center;
  font-size: ${(props): string => getFontSize(props.theme.introductoryTextSize)};
  margin: 0 auto;
  padding: 20px 25px 0;
  @media (max-width: 320px) {
    padding: 5px;
  }
  max-width: 1068px;
  div {
    background-color: ${(props): string => props.theme.itemColors.colourHeaderBackground};
    color: ${(props): string => props.theme.itemColors.colourTextOnBackground};
    padding: 12px;
    word-break: break-word;
  }
`

interface IItemGridContainerProps extends IAddToBasket {
  categories?: ICategoryResponse[]
  categoryImages?: boolean
  defaultCategory?: number
  giftItems: IGiftItemResponse[]
  currency: CurrencyCode
  introductoryText?: string
  introductoryTextSize?: FontSize
  font?: Font
  organisationColors?: Partial<IColorTheme>
  className?: string
  subCategoryFormat?: SubCategoryFormat
}
export default function ItemGridContainer(
  props: IItemGridContainerProps,
): ReactElement {
  const {
    categories,
    categoryImages,
    defaultCategory,
    giftItems,
    currency,
    introductoryText,
    introductoryTextSize,
    organisationColors,
    font,
    addToBasket,
    className,
    subCategoryFormat = SubCategoryFormat.Row,
  } = props

  const categoryToEmbed = defaultCategory
    && categories?.find((category) => category.id === defaultCategory)

  const isSub = categoryToEmbed && categoryToEmbed?.parent

  const initCategory = categoryToEmbed && (isSub ? categoryToEmbed.parent : categoryToEmbed.id)
  const initSub = categoryToEmbed && isSub && categoryToEmbed.id

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(initCategory)
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | undefined>(initSub)
  const [selectedOrder, setSelectedOrder] = useState<ItemOrder>()

  const itemColors = getItemColorTheme(organisationColors)

  const notFirstRender = useNotFirstRender()
  useEffect(() => {
    if (notFirstRender) scrollToTop()
    if (!selectedCategoryId) setSelectedOrder(undefined)
  }, [selectedCategoryId])

  const subCategories = categories
    ?.filter((category) => category.parent === selectedCategoryId)
    .sort((a, b) => {
      if (a.order < b.order) return -1
      if (a.order > b.order) return 1
      return 0
    })

  // hide controls for embedded categories
  const hideControls = defaultCategory || defaultCategory === 0

  const categoryGrid = !selectedCategoryId
    && categoryImages
    && categories
    && categories.length > 0
    && !hideControls && (
      <CategoryGrid
        categories={categories}
        setSelectedCategoryId={setSelectedCategoryId}
        colorTheme={itemColors}
      />
    )

  const isList = subCategoryFormat === SubCategoryFormat.List
  const showSubs = isList && subCategories && subCategories.length
  const hasTickets = giftItems.some((item) => isTicket(item))

  const showOrderSelect = !isList && !hasTickets ? (
    <OrderSelect
      selectedOrder={selectedOrder}
      setSelectedOrder={setSelectedOrder}
      colorTheme={itemColors}
    />
  ) : null

  // BUG: Can't use 'showSubs' here as TypeScript fails the build (need to update TS)
  const renderList = isList && subCategories && subCategories.length ? (
    <SubCategorySelect
      subCategories={subCategories}
      selectedSubCategoryId={selectedSubCategoryId}
      setSelectedSubCategoryId={setSelectedSubCategoryId}
      colorTheme={itemColors}
    />
  ) : (
    showOrderSelect
  )

  const renderListWhenEmbedded = showSubs ? (
    <EmbeddedSubcategorySelect>{renderList}</EmbeddedSubcategorySelect>
  ) : null

  const backButton = (
    <BackButton
      colourTheme={itemColors}
      onClick={(): void => setSelectedCategoryId(undefined)}
      keepLeftLonger={renderList === null}
      font={getFont(font)}
    />
  )

  return (
    <Styles theme={{ itemColors, font: getFont(font) }} className={className}>
      {introductoryText && (
        <IntroText theme={{ itemColors, introductoryTextSize }}>
          <div>{introductoryText}</div>
        </IntroText>
      )}
      {categoryGrid || (
        <div>
          {!hideControls && categories && categories.length ? (
            <Categories theme={{ shrink: showSubs || !isList }}>
              {categoryImages && backButton}
              <CategorySelect
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                setSelectedCategoryId={setSelectedCategoryId}
                setSelectedSubCategoryId={setSelectedSubCategoryId}
                colorTheme={itemColors}
              />
              {renderList}
            </Categories>
          ) : (
            renderListWhenEmbedded
          )}
          {subCategories && subCategories.length && !isList ? (
            <SubCategories
              subCategories={subCategories}
              selectedSubCategoryId={selectedSubCategoryId}
              setSelectedSubCategoryId={setSelectedSubCategoryId}
              colorTheme={itemColors}
              format={subCategoryFormat}
            />
          ) : null}
          <ItemGrid
            giftItems={filterGiftItems(
              giftItems,
              selectedSubCategoryId || selectedCategoryId,
              subCategories,
              selectedOrder,
            )}
            currency={currency}
            organisationColors={organisationColors}
            addToBasket={addToBasket}
            font={font}
          />
        </div>
      )}
    </Styles>
  )
}
